/**
 * Financial Health Check-Up — short, self-paced 13-question survey available
 * to ALL client plans (Free / DIY / Guided). Renders a card-style question
 * sheet, scores the result via the backend, and shows the user a detailed
 * interpretation with a pie-chart of their answer breakdown.
 *
 * A "Yes, contact me" tick-box at the end raises a service interest the
 * advisor sees in their notifications + service-interests list.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FaHeartbeat, FaSpinner, FaCheck, FaTimes, FaExclamationTriangle,
  FaTrophy, FaArrowRight, FaArrowLeft, FaRedo, FaShieldAlt, FaHandshake,
  FaInfoCircle, FaRegCircle, FaRegDotCircle, FaFileDownload
} from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import generateFinancialHealthPDF from '../utils/financialHealthPdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTIONS = [
  { value: 'no',  label: 'No',  color: 'text-red-300',    ring: 'border-red-500/40    hover:border-red-500'    , bg: 'bg-red-500/10' },
  { value: 'yes', label: 'Yes', color: 'text-green-300',  ring: 'border-green-500/40  hover:border-green-500'  , bg: 'bg-green-500/10' },
  { value: 'na',  label: 'NA',  color: 'text-gray-300',   ring: 'border-gray-500/40   hover:border-gray-500'   , bg: 'bg-gray-500/10' },
];

const BAND_STYLES = {
  critical: { color: '#F87171', ring: 'border-red-500/40',    bg: 'bg-red-500/10',    text: 'text-red-300',    icon: FaExclamationTriangle },
  medium:   { color: '#FBBF24', ring: 'border-amber-500/40',  bg: 'bg-amber-500/10',  text: 'text-amber-300',  icon: FaShieldAlt },
  low:      { color: '#34D399', ring: 'border-green-500/40',  bg: 'bg-green-500/10',  text: 'text-green-300',  icon: FaTrophy },
};

const QuestionCard = ({ q, index, total, value, onChange }) => (
  <div className="bg-[#0F172A] rounded-xl p-5 border border-[#2D5A8F]" data-testid={`question-${q.number}`}>
    <div className="flex items-start gap-3 mb-4">
      <div className="w-8 h-8 rounded-full bg-[#FFD700]/20 text-[#FFD700] text-sm font-bold flex items-center justify-center flex-shrink-0">
        {q.number}
      </div>
      <div className="flex-1">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Question {index + 1} of {total}</p>
        <p className="text-white text-sm sm:text-base leading-snug">{q.text}</p>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {OPTIONS.map(opt => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(q.number, opt.value)}
            data-testid={`q${q.number}-${opt.value}`}
            className={`py-2.5 px-3 rounded-lg border-2 ${opt.ring} ${selected ? `${opt.bg} ring-1 ring-offset-1 ring-offset-[#0F172A]` : ''} transition-all flex items-center justify-center gap-1.5`}
          >
            {selected
              ? <FaRegDotCircle className={opt.color} size={12} />
              : <FaRegCircle className="text-gray-600" size={12} />}
            <span className={`text-sm font-medium ${selected ? opt.color : 'text-gray-400'}`}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const ResultPanel = ({ result, client, advisor, onRetake }) => {
  const s = result.summary;
  const style = BAND_STYLES[s.risk_band] || BAND_STYLES.medium;
  const Icon = style.icon;

  // Pie chart data
  const pieData = [
    { name: 'Yes', value: s.yes_count, color: '#34D399' },
    { name: 'No',  value: s.no_count,  color: '#F87171' },
    { name: 'NA',  value: s.na_count,  color: '#9CA3AF' },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="result-panel">
      {/* Headline */}
      <div className={`rounded-xl p-6 border-2 ${style.ring} ${style.bg}`}>
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-full ${style.bg} flex items-center justify-center flex-shrink-0 border ${style.ring}`}>
            <Icon className={style.text} size={26} />
          </div>
          <div className="flex-1">
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Your Result</p>
            <h2 className={`text-xl sm:text-2xl font-bold ${style.text} mb-2`}>{s.risk_headline}</h2>
            <p className="text-gray-300 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: s.interpretation }} />
          </div>
        </div>
      </div>

      {/* Score + Chart */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#0F172A] rounded-xl p-5 border border-[#2D5A8F]">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">Score Breakdown</p>
          <div className="flex items-end gap-2 mb-4">
            <span className="text-5xl font-bold text-[#FFD700]" data-testid="result-score">{s.score}</span>
            <span className="text-gray-500 text-lg mb-1">/ {s.max_score}</span>
          </div>
          <div className="w-full bg-gray-700/50 rounded-full h-2 mb-1 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${s.score_percent}%`, backgroundColor: style.color }}
            />
          </div>
          <p className="text-gray-400 text-xs">{s.score_percent}% — each Yes answer = 10 marks</p>

          <div className="grid grid-cols-3 gap-2 mt-5">
            <div className="text-center p-2 rounded bg-green-500/10 border border-green-500/30">
              <p className="text-2xl font-bold text-green-300">{s.yes_count}</p>
              <p className="text-[10px] text-gray-400 uppercase">Yes</p>
            </div>
            <div className="text-center p-2 rounded bg-red-500/10 border border-red-500/30">
              <p className="text-2xl font-bold text-red-300">{s.no_count}</p>
              <p className="text-[10px] text-gray-400 uppercase">No</p>
            </div>
            <div className="text-center p-2 rounded bg-gray-500/10 border border-gray-500/30">
              <p className="text-2xl font-bold text-gray-300">{s.na_count}</p>
              <p className="text-[10px] text-gray-400 uppercase">NA</p>
            </div>
          </div>
        </div>

        <div className="bg-[#0F172A] rounded-xl p-5 border border-[#2D5A8F]">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">Answer Distribution</p>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                  {pieData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#0F172A', border: '1px solid #2D5A8F', borderRadius: 8 }}
                  itemStyle={{ color: '#FFFFFF' }}
                />
                <Legend wrapperStyle={{ color: '#9CA3AF', fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Consequences */}
      <div className="bg-[#0F172A] rounded-xl p-5 border border-[#2D5A8F]">
        <h3 className="text-white font-bold mb-3 flex items-center gap-2">
          <FaInfoCircle className="text-[#FFD700]" />
          Risks &amp; Consequences of Delay
        </h3>
        <ul className="space-y-2">
          {s.consequences.map((c) => (
            <li key={`consequence-${c.slice(0,18)}`} className="flex items-start gap-2 text-sm text-gray-300">
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0`} style={{ backgroundColor: style.color }} />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Detailed answers */}
      <div className="bg-[#0F172A] rounded-xl p-5 border border-[#2D5A8F]">
        <h3 className="text-white font-bold mb-3">Your Answers in Detail</h3>
        <div className="space-y-2">
          {result.detailed_answers.map(a => {
            const opt = OPTIONS.find(o => o.value === a.answer) || OPTIONS[2];
            return (
              <div key={a.number} className="flex items-start gap-3 py-2 border-b border-[#2D5A8F]/40 last:border-0">
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${opt.bg} ${opt.color} border ${opt.ring}`}>{opt.label}</span>
                <span className="text-gray-300 text-sm flex-1">
                  <span className="text-gray-500">Q{a.number}.</span> {a.text}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Interested-in-review confirmation */}
      {result.interested_in_review && (
        <div className="bg-purple-500/10 border border-purple-500/40 rounded-xl p-4 flex items-start gap-3" data-testid="interested-confirmation">
          <FaCheck className="text-purple-300 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-purple-300 font-bold text-sm">Thank you! Your interest has been recorded.</p>
            <p className="text-gray-400 text-xs mt-1">
              Your advisor will reach out shortly to schedule a personalised consultation for a detailed Arth Kundli financial health review.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => generateFinancialHealthPDF({ result, client, advisor })}
          data-testid="download-pdf-btn"
          className="flex-1 py-3 px-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#FFD700]/10"
        >
          <FaFileDownload />
          Download Report (PDF)
        </button>
        <button
          onClick={onRetake}
          data-testid="retake-test-btn"
          className="flex-1 py-3 px-4 border border-[#FFD700]/40 text-[#FFD700] font-bold rounded-lg hover:bg-[#FFD700]/10 transition-all flex items-center justify-center gap-2"
        >
          <FaRedo />
          Retake the Assessment
        </button>
      </div>
    </div>
  );
};

const FinancialHealthCheckup = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [latestResult, setLatestResult] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [advisorInfo, setAdvisorInfo] = useState(null);
  const [answers, setAnswers] = useState({});
  const [interested, setInterested] = useState(false);
  const [view, setView] = useState('intro'); // 'intro' | 'questions' | 'result'
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);

  const QUESTIONS_PER_PAGE = 5;

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/client/financial-health-checkup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuestions(data.questions || []);
      setLatestResult(data.latest || null);
      setClientInfo(data.client || null);
      setAdvisorInfo(data.advisor || null);
      if (data.latest) setView('intro');
    } catch (e) {
      setError('Unable to load the assessment. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAnswer = (num, value) => {
    setAnswers(prev => ({ ...prev, [num]: value }));
  };

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);
  const currentPageQuestions = questions.slice(page * QUESTIONS_PER_PAGE, (page + 1) * QUESTIONS_PER_PAGE);
  const allAnswered = questions.length > 0 && questions.every(q => answers[q.number]);
  const currentPageAnswered = currentPageQuestions.every(q => answers[q.number]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/client/financial-health-checkup`,
        { answers, interested_in_review: interested },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLatestResult(data);
      setView('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit the assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const startNewAssessment = () => {
    setAnswers({});
    setInterested(false);
    setPage(0);
    setView('questions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="health-checkup-loading">
        <FaSpinner className="animate-spin text-[#FFD700] text-3xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-health-checkup-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
          <FaHeartbeat className="text-pink-400 text-2xl" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Financial Health Check-Up</h1>
          <p className="text-gray-400 text-sm">A quick 13-question scan of your overall financial wellness</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-red-300 text-sm" data-testid="checkup-error">{error}</div>
      )}

      {view === 'intro' && (
        <div className="space-y-4">
          {latestResult && (
            <div className="bg-[#0F172A] rounded-xl p-5 border border-[#FFD700]/30" data-testid="last-result-card">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Your Last Assessment</p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className={`text-lg font-bold ${BAND_STYLES[latestResult.summary.risk_band].text}`}>{latestResult.summary.risk_headline}</p>
                  <p className="text-gray-500 text-xs">
                    Score {latestResult.summary.score}/{latestResult.summary.max_score} • {new Date(latestResult.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => generateFinancialHealthPDF({ result: latestResult, client: clientInfo, advisor: advisorInfo })}
                    className="px-3 py-1.5 text-xs font-bold text-[#0A1929] bg-gradient-to-r from-[#D4AF37] to-[#FFD700] rounded hover:from-[#FFD700] hover:to-[#D4AF37] flex items-center gap-1.5"
                    data-testid="intro-download-pdf-btn"
                  >
                    <FaFileDownload size={11} /> PDF
                  </button>
                  <button
                    onClick={() => setView('result')}
                    className="px-3 py-1.5 text-xs font-medium text-[#FFD700] border border-[#FFD700]/40 rounded hover:bg-[#FFD700]/10"
                    data-testid="view-last-result-btn"
                  >
                    View Result
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-[#0F172A] rounded-xl p-6 border border-[#2D5A8F]">
            <h2 className="text-white text-lg font-bold mb-3">How it works</h2>
            <ul className="space-y-2 text-gray-300 text-sm mb-5">
              <li className="flex items-start gap-2"><FaCheck className="text-green-400 mt-1 flex-shrink-0" size={12} /> 13 simple questions about your protection, savings &amp; investments.</li>
              <li className="flex items-start gap-2"><FaCheck className="text-green-400 mt-1 flex-shrink-0" size={12} /> Three options for each: <span className="text-green-300 font-bold">Yes</span>, <span className="text-red-300 font-bold">No</span>, <span className="text-gray-300 font-bold">NA</span>.</li>
              <li className="flex items-start gap-2"><FaCheck className="text-green-400 mt-1 flex-shrink-0" size={12} /> You'll get a personalised risk band, score, and detailed interpretation.</li>
              <li className="flex items-start gap-2"><FaCheck className="text-green-400 mt-1 flex-shrink-0" size={12} /> Takes about 3 minutes. Available to all plans — Free, DIY and Guided.</li>
            </ul>
            <button
              onClick={startNewAssessment}
              data-testid="start-checkup-btn"
              className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-xl hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center gap-2"
            >
              Start Check-Up <FaArrowRight />
            </button>
          </div>
        </div>
      )}

      {view === 'questions' && (
        <div className="space-y-5">
          {/* Progress */}
          <div className="bg-[#0F172A] rounded-full h-2 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] transition-all" style={{ width: `${(Object.keys(answers).length / questions.length) * 100}%` }} />
          </div>
          <p className="text-xs text-gray-500 text-center -mt-3" data-testid="progress-text">{Object.keys(answers).length} of {questions.length} answered • Page {page + 1} of {totalPages}</p>

          {currentPageQuestions.map((q, idx) => (
            <QuestionCard
              key={q.number}
              q={q}
              index={page * QUESTIONS_PER_PAGE + idx}
              total={questions.length}
              value={answers[q.number]}
              onChange={handleAnswer}
            />
          ))}

          {page === totalPages - 1 && (
            <label className="flex items-start gap-3 bg-[#1A1B3D] border border-purple-500/40 rounded-xl p-4 cursor-pointer hover:bg-[#23244F] transition-colors" data-testid="interested-checkbox-row">
              <input
                type="checkbox"
                checked={interested}
                onChange={e => setInterested(e.target.checked)}
                className="mt-1 w-4 h-4 accent-purple-400"
                data-testid="interested-checkbox"
              />
              <div className="text-sm">
                <span className="text-purple-200 font-bold">Yes, I am interested in doing a detailed financial health review using Arth Kundli. Please contact me.</span>
                <p className="text-gray-400 text-xs mt-1">By ticking this, your assigned advisor will be notified and reach out for a personalised consultation.</p>
              </div>
            </label>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <button
              onClick={() => { setPage(p => Math.max(0, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              disabled={page === 0}
              data-testid="prev-page-btn"
              className="px-4 py-2 text-sm border border-gray-600 text-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-700 transition-all flex items-center gap-2"
            >
              <FaArrowLeft /> Previous
            </button>
            {page < totalPages - 1 ? (
              <button
                onClick={() => { setPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={!currentPageAnswered}
                data-testid="next-page-btn"
                className="px-4 py-2 text-sm bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Next <FaArrowRight />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
                data-testid="submit-checkup-btn"
                className="px-5 py-2 text-sm bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" /> Scoring...
                  </>
                ) : (
                  <>
                    <FaCheck /> Submit &amp; See My Result
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {view === 'result' && latestResult && (
        <>
          <ResultPanel result={latestResult} client={clientInfo} advisor={advisorInfo} onRetake={startNewAssessment} />

          {/* Consultation CTA — always shown after a result so the user can opt-in
              even if they didn't tick the box during submission. */}
          {!latestResult.interested_in_review && (
            <div className="bg-gradient-to-br from-[#1A2942] to-[#0F172A] border border-[#FFD700]/40 rounded-xl p-5 sm:p-6" data-testid="post-result-cta">
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <FaHandshake className="text-[#FFD700] text-3xl mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-white font-bold text-base sm:text-lg mb-1">Book a Personalised Consultation</h3>
                  <p className="text-gray-400 text-sm mb-3">
                    Get a detailed Arth Kundli financial health review from a qualified consultant — tailored to your unique situation, goals, and family needs.
                  </p>
                  <p className="text-[10px] text-gray-500 italic mb-3 border-l-2 border-[#FFD700]/30 pl-2 leading-relaxed">
                    The membership fee is for financial wellness education &amp; financial wellness tools only. No fees are charged by the platform for any consultation or advice by a financial consultant or planner.
                  </p>
                  <button
                    onClick={startNewAssessment}
                    data-testid="post-result-retake-btn"
                    className="text-xs text-[#FFD700] underline hover:text-[#D4AF37]"
                  >
                    Or retake the assessment with the "contact me" box ticked
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FinancialHealthCheckup;
